type CalendarDate = {
  readonly year: number;
  readonly month: number;
  readonly day: number;
};

const UTC_ZONE = "UTC";
const OFFSET_PATTERN = /^(?:GMT|UTC)(?:([+-])(\d{2}):?(\d{2}))?$/;

function browserTimezone(): string {
  if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") {
    return UTC_ZONE;
  }

  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof zone !== "string" || zone.length === 0) return UTC_ZONE;
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone;
  } catch (error: unknown) {
    if (error instanceof RangeError || error instanceof TypeError) return UTC_ZONE;
    throw error;
  }
}

function isValidTimezone(zone: string): boolean {
  if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") return false;

  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch (error: unknown) {
    if (error instanceof RangeError || error instanceof TypeError) return false;
    throw error;
  }
}

export function resolveUserTimezone(profileTz: string | undefined): string {
  const fallback = browserTimezone();
  if (profileTz === undefined || profileTz === "auto") return fallback;
  return isValidTimezone(profileTz) ? profileTz : fallback;
}

function partValue(parts: readonly Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  const part = parts.find((candidate) => candidate.type === type);
  if (part === undefined) throw new RangeError(`Intl did not return ${type}`);
  return part.value;
}

function calendarDateInZone(timestampMs: number, zone: string): CalendarDate {
  const parts = new Intl.DateTimeFormat("en-US", {
    calendar: "gregory",
    numberingSystem: "latn",
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(timestampMs));

  return {
    year: Number(partValue(parts, "year")),
    month: Number(partValue(parts, "month")),
    day: Number(partValue(parts, "day")),
  };
}

function offsetAt(timestampMs: number, zone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    calendar: "gregory",
    numberingSystem: "latn",
    timeZone: zone,
    timeZoneName: "longOffset",
  }).formatToParts(new Date(timestampMs));
  const offsetName = partValue(parts, "timeZoneName");
  const match = OFFSET_PATTERN.exec(offsetName);
  if (match === null) throw new RangeError(`Unsupported timezone offset: ${offsetName}`);
  if (match[1] === undefined) return 0;

  const direction = match[1] === "+" ? 1 : -1;
  return direction * (Number(match[2]) * 60 + Number(match[3])) * 60_000;
}

function midnightForCalendarDate(date: CalendarDate, zone: string): number {
  const wallMidnightAsUtc = Date.UTC(date.year, date.month - 1, date.day);
  const firstCandidate = wallMidnightAsUtc - offsetAt(wallMidnightAsUtc, zone);
  return wallMidnightAsUtc - offsetAt(firstCandidate, zone);
}

export function dayStartInZone(timestampMs: number, zone: string): number {
  const resolvedZone = resolveUserTimezone(zone);
  return midnightForCalendarDate(calendarDateInZone(timestampMs, resolvedZone), resolvedZone);
}

export function addDaysInZone(timestampMs: number, days: number, zone: string): number {
  const resolvedZone = resolveUserTimezone(zone);
  const date = calendarDateInZone(timestampMs, resolvedZone);
  const targetDate = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return midnightForCalendarDate(
    {
      year: targetDate.getUTCFullYear(),
      month: targetDate.getUTCMonth() + 1,
      day: targetDate.getUTCDate(),
    },
    resolvedZone,
  );
}

export function yearMonthInZone(timestampMs: number, zone: string): string {
  const date = calendarDateInZone(timestampMs, resolveUserTimezone(zone));
  return `${date.year}-${String(date.month).padStart(2, "0")}`;
}
